import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLabels } from '../context/LabelsContext';
import api from '../services/api';
import '../styles/Layout.css';

const Layout = ({ children }) => {
  const { user, logout, theme, toggleTheme } = useAuth();
  const { getLabel } = useLabels();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [configOpen, setConfigOpen] = useState(true);
  const [limitesPlan, setLimitesPlan] = useState({
    max_servicios: 1,
    max_clientes: 1,
    max_usuarios: 1,
    max_profesionales: 1,
    tiene_sucursales: false
  });
  const [diasRestantes, setDiasRestantes] = useState(null);
  const [mostrarAlertaHeader, setMostrarAlertaHeader] = useState(false);
  const [enGracia, setEnGracia] = useState(false);
  const [diasVencido, setDiasVencido] = useState(0);
  const [esTrial, setEsTrial] = useState(false);
  const [anticiposBadge, setAnticiposBadge] = useState(0);

  const esSuperAdmin = user?.rol === 'superadmin';

  useEffect(() => {
    if (!esSuperAdmin && user?.tenant) {
      fetchLimitesPlan();
      fetchDiasRestantes();
      fetchAnticiposBadge();
      const interval = setInterval(fetchDiasRestantes, 300000);
      const intervalBadge = setInterval(fetchAnticiposBadge, 300000);
      return () => { clearInterval(interval); clearInterval(intervalBadge); };
    }
  }, [esSuperAdmin, user]);

  useEffect(() => {
    const savedState = localStorage.getItem('sidebarCollapsed');
    if (savedState !== null) {
      setSidebarCollapsed(savedState === 'true');
    }
  }, []);

  useEffect(() => {
    const handler = () => fetchAnticiposBadge();
    window.addEventListener('anticipos-updated', handler);
    return () => window.removeEventListener('anticipos-updated', handler);
  }, []);

  const toggleSidebarCollapse = () => {
    const newState = !sidebarCollapsed;
    setSidebarCollapsed(newState);
    localStorage.setItem('sidebarCollapsed', newState);
  };

  const fetchAnticiposBadge = async () => {
    try {
      const { data } = await api.get('/citas/pendiente-pago');
      setAnticiposBadge(data.length);
    } catch {
      // silently fail
    }
  };

  const fetchLimitesPlan = async () => {
    try {
      const { data } = await api.get('/tenant/plan-info');
      setLimitesPlan({
        max_servicios: data.plan?.max_servicios ?? 1,
        max_clientes: data.plan?.max_clientes ?? 1,
        max_usuarios: data.plan?.max_usuarios ?? 1,
        max_profesionales: data.plan?.max_profesionales ?? 1,
        tiene_sucursales: user.tenant?.tiene_sucursales || false
      });
    } catch (error) {
      console.error('Error al cargar límites del plan:', error);
    }
  };

  const fetchDiasRestantes = async () => {
    try {
      const { data } = await api.get('/planes/estado');
      if (data.tiene_suscripcion) {
        setDiasRestantes(data.dias_vencimiento);
        setEnGracia(data.en_gracia || false);
        setDiasVencido(data.dias_vencido || 0);
        setEsTrial(data.es_trial || false);
        setMostrarAlertaHeader(data.dias_vencimiento <= 3 && !data.pagado && !data.en_gracia);
      } else {
        setDiasRestantes(null);
        setMostrarAlertaHeader(false);
        setEnGracia(false);
        setDiasVencido(0);
        setEsTrial(false);
      }
    } catch {
      setDiasRestantes(null);
      setMostrarAlertaHeader(false);
      setEnGracia(false);
      setDiasVencido(0);
      setEsTrial(false);
    }
  };

  const puedeMostrar = (modulo) => {
    if (esSuperAdmin) return false;
    switch (modulo) {
      case 'profesionales': return limitesPlan.max_profesionales > 0;
      case 'servicios':     return limitesPlan.max_servicios > 0;
      case 'clientes':      return limitesPlan.max_clientes > 0;
      case 'usuarios':      return limitesPlan.max_usuarios > 0;
      case 'sucursales':    return limitesPlan.tiene_sucursales > 0;
      default:              return true;
    }
  };

  let mainMenuItems = [];

  if (esSuperAdmin) {
    mainMenuItems = [
      { path: '/',                    icon: '📊', label: 'Dashboard',     visible: true },
      { path: '/admin/planes',        icon: '📦', label: 'Planes',        visible: true },
      { path: '/admin/suscripciones', icon: '💰', label: 'Suscripciones', visible: true },
      { path: '/admin/logs',          icon: '🔍', label: 'Logs',          visible: true },
      { path: '/historial',           icon: '📋', label: 'Historial',     visible: true },
    ];
  } else {
    const esAdmin = user?.rol === 'admin';
    mainMenuItems = [
      { path: '/',               icon: '📊', label: 'Dashboard',     visible: true },
      { path: '/citas',          icon: '📅', label: 'Citas',          visible: true, badge: anticiposBadge || null },
      { path: '/crm/prospectos', icon: '🤝', label: 'CRM',            visible: esAdmin },
      { path: '/catalogos',      icon: '🛍️', label: 'Catálogos',      visible: esAdmin },
      { path: '/difusion',       icon: '📢', label: 'Difusión',       visible: (esAdmin || !!user?.puede_enviar_difusion) && !!user?.tenant?.addon_envios },
      { path: '/clientes',       icon: '👥', label: 'Clientes',       visible: puedeMostrar('clientes') && (esAdmin || !!user?.puede_ver_clientes) },
      { path: '/servicios',      icon: '💼', label: 'Servicios',      visible: puedeMostrar('servicios') },
      { path: '/profesionales',  icon: '👨‍💼', label: getLabel('profesionales'),  visible: puedeMostrar('profesionales') },
      { path: '/reportes',       icon: '📈', label: 'Reportes',       visible: esAdmin || !!user?.puede_ver_reportes },
    ];
  }

  const esAdmin = user?.rol === 'admin';
  const configMenuItems = !esSuperAdmin ? [
    { path: '/datos-negocio', icon: '🏪', label: 'Mi Negocio',   visible: esAdmin },
    { path: '/usuarios',      icon: '👥', label: 'Usuarios',     visible: puedeMostrar('usuarios') && esAdmin },
    { path: '/sucursales',    icon: '🏢', label: 'Sucursales',   visible: esAdmin },
    { path: '/horarios',      icon: '🕒', label: 'Horarios',     visible: esAdmin },
    { path: '/configuracion', icon: '⚙️', label: 'Preferencias', visible: esAdmin || !!user?.puede_configurar },
    { path: '/historial',    icon: '📋', label: 'Historial',    visible: esAdmin || !!user?.puede_ver_historial },
    { path: '/whatsapp',     icon: '💬', label: 'WhatsApp',     visible: esAdmin },
  ].filter(item => item.visible) : [];

  const adminMenuItems = [];

  const tieneConfiguracion = configMenuItems.length > 0;

  /* Iniciales del usuario para el avatar */
  const initials = [user?.nombre, user?.apellido]
    .filter(Boolean)
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) || 'U';

  return (
    <div className={`layout ${theme === 'oscuro' ? 'dark-mode' : ''}`}>

      {/* ── NAVBAR ── */}
      <nav className="navbar">
        <div className="navbar-left">
          <button className="menu-toggle" onClick={() => setSidebarOpen(!sidebarOpen)} aria-label="Menú">
            <span className="hamburger-icon">
              <span /><span /><span />
            </span>
          </button>
        </div>

        <div className="navbar-right">
          {/* Toggle tema */}
          <button className="icon-btn" onClick={toggleTheme} title="Cambiar tema">
            {theme === 'claro' ? '🌙' : '☀️'}
          </button>

          {/* Período de gracia — suscripción vencida pero aún con acceso */}
          {!esSuperAdmin && enGracia && (
            <div className="plan-badge plan-badge--grace">
              <span>⚠️</span>
              <span>{diasVencido === 0 ? 'Suscripción Vencida' : `${diasVencido} ${diasVencido === 1 ? 'día' : 'días'} vencida`}</span>
              <Link to="/suscripcion/pagar" className="plan-badge__cta">Renovar suscripción</Link>
            </div>
          )}

          {/* Días restantes — activo y sin período de gracia */}
          {!esSuperAdmin && !enGracia && diasRestantes !== null && diasRestantes > 0 && (
            <div className={`plan-badge ${mostrarAlertaHeader ? 'plan-badge--warning' : 'plan-badge--ok'}`}>
              <span className="plan-badge__icon">{esTrial ? '🧪' : '⏰'}</span>
              <span className="plan-badge__days">{diasRestantes}</span>
              <span className="plan-badge__label">{diasRestantes === 1 ? 'día' : 'días'} {esTrial ? 'de trial' : 'restantes'}</span>
              {mostrarAlertaHeader && (
                <Link to="/suscripcion/pagar" className="plan-badge__cta">
                  {esTrial ? 'Contratar plan' : 'Pagar'}
                </Link>
              )}
            </div>
          )}

          {/* Suscripción / Trial vencido y fuera de gracia — acceso bloqueado */}
          {!esSuperAdmin && !enGracia && diasRestantes !== null && diasRestantes <= 0 && (
            <div className="plan-badge plan-badge--expired">
              <span>⚠️</span>
              <span>{esTrial ? 'Trial vencido' : 'Suscripción vencida'}</span>
              <Link to="/suscripcion/pagar" className="plan-badge__cta">
                {esTrial ? 'Contratar plan' : 'Renovar'}
              </Link>
            </div>
          )}

          {/* Código de empresa */}
          {!esSuperAdmin && user?.tenant?.codigo && (
            <div className="whatsapp-code">
              <span>🏷️</span>
              <span><strong>Código de empresa:</strong> {user.tenant.codigo}</span>
            </div>
          )}

          {/* Código WhatsApp */}
          {!esSuperAdmin && user?.tenant?.plan === 'Profesional' && (
            <div className="whatsapp-code">
              <span>📱</span>
              <span><strong>WhatsApp:</strong> {user.tenant.codigo}</span>
            </div>
          )}

          {/* Divider */}
          <div className="nav-divider" />

          {/* User chip */}
          <div className="user-chip">
            <div className="user-avatar">{initials}</div>
            <div className="user-info">
              <span className="user-name">{user?.nombre} {user?.apellido}</span>
              <span className="user-role">{esSuperAdmin ? 'Super Admin' : (user?.tenant?.nombre || 'Usuario')}</span>
            </div>
          </div>

          <button className="logout-btn" onClick={logout}>Salir</button>
        </div>
      </nav>

      {/* ── SIDEBAR ── */}
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''} ${sidebarCollapsed ? 'collapsed' : ''}`}>

        {/* Cabecera sidebar — clic para colapsar/expandir */}
        <div className="sidebar-header" onClick={toggleSidebarCollapse} title={sidebarCollapsed ? 'Expandir menú' : 'Colapsar menú'}>
          <span className="sidebar-brand-icon">🎯</span>

          {!sidebarCollapsed && (
            <>
              <div className="sidebar-brand-info">
                <span className="sidebar-brand-name">FactuTracks</span>
                {!esSuperAdmin && (
                  <span className="tenant-plan-badge">Agenda</span>
                )}
              </div>
              <span className="sidebar-brand-arrow">‹</span>
            </>
          )}
        </div>

        {/* Navegación */}
        <nav className="sidebar-nav">
          {/* Menú principal */}
          {mainMenuItems.filter(i => i.visible).map(item => (
            <Link
              key={item.path}
              to={item.path}
              className={`sidebar-link ${location.pathname === item.path ? 'active' : ''}`}
              onClick={() => setSidebarOpen(false)}
              title={item.label}
            >
              <span style={{ position:'relative', display:'inline-flex' }}>
                <span className="sidebar-icon">{item.icon}</span>
                {item.badge && sidebarCollapsed && (
                  <span style={{ position:'absolute', top:'-0.2rem', right:'-0.3rem', width:'0.5rem', height:'0.5rem', background:'#ef4444', borderRadius:'999px' }} />
                )}
              </span>
              {!sidebarCollapsed && <span className="sidebar-link-label">{item.label}</span>}
              {!sidebarCollapsed && item.badge && (
                <span style={{ marginLeft:'auto', background:'#ef4444', color:'#fff', borderRadius:'999px', fontSize:'0.65rem', fontWeight:'700', padding:'0.15rem 0.4rem', lineHeight:'1.4', minWidth:'1.1rem', textAlign:'center' }}>
                  {item.badge}
                </span>
              )}
              {!sidebarCollapsed && location.pathname === item.path && (
                <span className="active-indicator" />
              )}
            </Link>
          ))}

          {/* Admin items */}
          {adminMenuItems.map(item => (
            <Link
              key={item.path}
              to={item.path}
              className={`sidebar-link ${location.pathname === item.path ? 'active' : ''}`}
              onClick={() => setSidebarOpen(false)}
              title={item.label}
            >
              <span className="sidebar-icon">{item.icon}</span>
              {!sidebarCollapsed && <span className="sidebar-link-label">{item.label}</span>}
            </Link>
          ))}

          {/* Divider */}
          {!esSuperAdmin && tieneConfiguracion && (
            <div className={sidebarCollapsed ? 'sidebar-divider-mini' : 'sidebar-divider'} />
          )}

          {/* Configuración */}
          {!esSuperAdmin && tieneConfiguracion && (
            <>
              <div
                className={`sidebar-section-header ${sidebarCollapsed ? 'collapsed' : ''}`}
                onClick={() => !sidebarCollapsed && setConfigOpen(!configOpen)}
                title={sidebarCollapsed ? 'Configuración' : ''}
              >
                <div className="sidebar-section-inner">
                  <span className="sidebar-icon">⚙️</span>
                  {!sidebarCollapsed && <span>Configuración</span>}
                </div>
                {!sidebarCollapsed && (
                  <span className={`sidebar-section-arrow ${configOpen ? 'open' : ''}`}>▾</span>
                )}
              </div>

              <div className={`sidebar-submenu ${configOpen && !sidebarCollapsed ? 'open' : 'closed'}`}>
                {configMenuItems.map(item => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`sidebar-link sidebar-link--sub ${location.pathname === item.path ? 'active' : ''}`}
                    onClick={() => setSidebarOpen(false)}
                    style={{ paddingLeft: sidebarCollapsed ? 0 : '2.5rem' }}
                    title={item.label}
                  >
                    <span className="sidebar-icon">{item.icon}</span>
                    {!sidebarCollapsed && <span className="sidebar-link-label">{item.label}</span>}
                  </Link>
                ))}
              </div>
            </>
          )}
        </nav>

        {/* ── Sidebar bottom ── */}
        <div className="sidebar-bottom">
          {/* SuperAdmin badge en la parte inferior */}
          {esSuperAdmin && (
            <div className="sidebar-superadmin-chip">
              <span className="sidebar-superadmin-crown">👑</span>
              {!sidebarCollapsed && (
                <div className="sidebar-superadmin-info">
                  <span className="sidebar-superadmin-name">{user?.nombre} {user?.apellido}</span>
                  <span className="sidebar-superadmin-role">Super Admin</span>
                </div>
              )}
            </div>
          )}

          {/* User chip normal */}
          {!esSuperAdmin && (
            <div className="sidebar-user-chip">
              <div className="sidebar-user-avatar">{initials}</div>
              {!sidebarCollapsed && (
                <div className="sidebar-user-info">
                  <span className="sidebar-user-name">{user?.nombre} {user?.apellido}</span>
                  <span className="sidebar-user-role">{user?.tenant?.nombre || 'Usuario'}</span>
                  {user?.tenant?.plan && (
                    <span className="sidebar-user-role" style={{ fontSize: '0.7rem', opacity: 0.7 }}>{user.tenant.plan}</span>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </aside>
      {sidebarOpen && (
        <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />
      )}

      {/* ── CONTENIDO ── */}
      <main className={`main-content ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        {children}
      </main>
    </div>
  );
};

export default Layout;